import type { NavigationAsync } from "../index.js";
import type { FunctionImplementation } from "../../nexus/function/function.types.js";
import type { NavigationInput } from "../lib/navigation.types.js";
import type { NavigationParameters, ParameterRecord, ParameterValue } from "./params.types.js";
import type { ObjectToEntries, ObjectToEntry } from "../lib/utils.js";

function locationToParams(
  location: NavigationInput,
  type: "search" | "hash",
): [location: URL, search: URLSearchParams, hash: URLSearchParams] {
  let url: URL;

  /*                       */
  if (typeof location === "string")
    if (location.startsWith("http")) url = new URL(location);
    else {
      url = new URL(window.location.href);

      if (type === "hash") url.hash = location;
      else url.search = location;
    }
  else url = new URL(location.toString());

  /*                                                  */
  return [url, url.searchParams, new URLSearchParams(url.hash.slice(1))];
}

function coerceValue(value: string): unknown {
  if (value === "undefined") return undefined;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

const uncoerceValue = (value: unknown): string =>
  typeof value === "string" ? value : JSON.stringify(value);

/**
 *
 *
 *
 *
 *
 */
function reduceToRecord<C extends [PropertyKey, ParameterValue]>(
  acc: Record<PropertyKey, ParameterValue>,
  [key, value]: C,
): Record<PropertyKey, ParameterValue> {
  if (!(key in acc)) {
    acc[key] = value;
  } else if (Array.isArray(acc[key])) {
    acc[key].push(value as string);
  } else {
    acc[key] = [acc[key] as string, value as string];
  }

  return acc;
}

/**
 *
 *
 *
 *
 *
 */
function reduceToObject<C extends [PropertyKey, ParameterValue]>(
  acc: Record<PropertyKey, unknown>,
  [key, value]: C,
): Record<PropertyKey, unknown> {
  const coerced = coerceValue(value as string);

  if (!(key in acc)) {
    acc[key] = coerced;
  } else if (Array.isArray(acc[key])) {
    acc[key].push(coerced);
  } else {
    acc[key] = [acc[key] as string, coerced];
  }

  return acc;
}

const isValidKey = (key: string, filter: string[]): boolean => filter.includes(key);

function createParams<P extends ParameterRecord>(
  raw: URLSearchParams,
  type: "search" | "hash",
  url: URL,
  filter: string[] = [],
): NavigationParameters<P> {
  const res: NavigationParameters<P> = {
    *[Symbol.iterator]() {
      if (filter.length === 0)
        for (const [key, value] of raw) yield [key, value] as ObjectToEntry<P>;
      else
        for (const [key, value] of raw) {
          if (isValidKey(key, filter)) yield [key, value] as ObjectToEntry<P>;
        }
    },

    get [Symbol.toStringTag]() {
      return `${type[0].toUpperCase()}${type.slice(1)}Parameters`;
    },

    keys: raw.keys.bind(raw) as never,
    values: raw.values.bind(raw) as never,
    append: raw.append.bind(raw) as never,
    set: raw.set.bind(raw) as never,
    delete: raw.delete.bind(raw) as never,
    get: raw.get.bind(raw) as never,
    getAll: raw.getAll.bind(raw) as never,
    has: raw.has.bind(raw) as never,
    entries: raw.entries.bind(raw) as never,

    clear: () => {
      /*                                         */
      if (filter.length === 0) for (const key of raw.keys()) raw.delete(key);
      else for (const key of raw.keys()) if (isValidKey(key, filter)) raw.delete(key);
    },

    toEntries: () => [...res],
    fromEntries: (entries) => {
      res.clear();
      for (const [key, value] of entries) {
        if (Array.isArray(value)) value.forEach((v) => raw.append(key as string, uncoerceValue(v)));
        else raw.set(key as string, uncoerceValue(value));
      }
    },

    toRecord: () => res.toEntries().reduce(reduceToRecord, {}) as never,
    toObject: () => res.toEntries().reduce(reduceToObject, {}) as never,

    coerce: (key) => (res.has(key) ? (coerceValue(res.get(key)!) as never) : null),
    coerceAll: (key) => {
      const values = res.getAll(key);
      return values ? values.map((v) => coerceValue(v) as never) : /*                */ [];
    },

    appendMany: (...entries) => entries.forEach(([key, value]) => raw.append(key as string, value)),

    fromObject: (entries) => res.fromEntries(Object.entries(entries) as ObjectToEntries<unknown>),

    parse: (schema, params) => schema.parse(res.toRecord(), params),
    parseSafe: (schema, params) => schema.safeParse(res.toRecord(), params),

    type,
    toString: () => `${type === "hash" ? "#" : "?"}${decodeURI(raw.toString())}`,
    toUrl: () => {
      const resUrl = new URL(url);
      resUrl[type] = decodeURI(raw.toString());
      return resUrl;
    },
    clone: () => createParams(new URLSearchParams(raw), type, url, filter),
  };

  return res;
}

/**
 *
 */
export const hashParams = ((location, filter) => {
  const [url, , raw] = locationToParams(location, "hash");
  return createParams(raw, "hash", url, filter);
}) satisfies FunctionImplementation<NavigationAsync["hashParams"]>;

/**
 *
 */
export const searchParams = ((location, filter) => {
  const [url, raw] = locationToParams(location, "search");
  return createParams(raw, "search", url, filter);
}) satisfies FunctionImplementation<NavigationAsync["searchParams"]>;
